import { lazy } from 'react';
import type { ComponentType } from 'react';
import { puxarShellNovo } from '../pwa/swRegistration';

/**
 * Chave de sessão que impede o laço.
 *
 * Se o chunk falhar de novo DEPOIS de já termos puxado o shell novo, o problema
 * não é código velho: é arquivo que realmente não está lá, rede caída ou deploy
 * quebrado. Aí o erro tem que aparecer, e não virar recarregamento infinito com
 * a tela piscando na cara do usuário.
 */
const JA_TENTOU = 'meugantt_shell_recarregado';

function jaTentou(): boolean {
  try { return sessionStorage.getItem(JA_TENTOU) === '1'; } catch { return false; }
}

function marcarTentativa(): void {
  try { sessionStorage.setItem(JA_TENTOU, '1'); } catch { /* modo privado */ }
}

function limparTentativa(): void {
  try { sessionStorage.removeItem(JA_TENTOU); } catch { /* modo privado */ }
}

/**
 * `React.lazy` que se recupera de chunk que sumiu no deploy.
 *
 * O caso: publicamos, o Vite apaga os arquivos de hash antigo, e quem tem a aba
 * aberta (ou o shell no cache do service worker) pede um chunk que não existe
 * mais. O import dinâmico rejeita e o usuário lê "Erro ao carregar módulo" numa
 * instalação que estava funcionando trinta segundos antes.
 *
 * A resposta certa não é mostrar erro: é buscar a versão nova, porque ela existe
 * e o usuário não tem nada a decidir aqui. Uma vez por sessão, só.
 */
// O genérico espelha a assinatura do próprio `React.lazy`: os módulos aqui têm
// props de tipos diferentes entre si, e um `unknown` no lugar do `any` recusaria
// todo componente que recebe prop.
export function lazyModule<T extends ComponentType<any>>(
  carregar: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const modulo = await carregar();
      // Chegou: a sessão está sã de novo e um deploy futuro merece nova chance.
      limparTentativa();
      return modulo;
    } catch (err) {
      if (jaTentou()) throw err;

      marcarTentativa();
      void puxarShellNovo();

      // Promise que nunca resolve, de propósito: a página está indo embora e
      // resolver aqui faria o React renderizar um fallback que ninguém verá.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
